/**
 * SaleInvoices.tsx — lista faktur sprzedaży na ConfigurableTable; kolumny DATA-DRIVEN z katalogu.
 *
 * Klon `PurchaseInvoices.tsx` (strona wzorcowa silnika list) dla kierunku sprzedaży.
 *
 * Kolumny rdzenne buduje `buildCoreColumns` z katalogu pól (`GET /system-modules/sale_invoices/
 * fields`): pole z „Pokaż na liście" (showInList) + typ renderowalny = kolumna; klucz w wierszu
 * odpowiedzi wyliczany mechanicznie ze sluga (camelCase), więc ZERO mapy slug→klucz per strona.
 * Konfiguracja kolumn faktur sprzedaży (nadpisania, wykluczenia, remap sortu, slugi `?columns=`,
 * mapa id→slug, typ wiersza) żyje w `lib/documents/sale-invoice-columns` — wspólna ze stroną i z
 * sekcją kokpitu decyzji; kolumny procesu buduje wspólne `lib/documents/process-columns`.
 * Garstka kolumn ma bogaty render przez rejestr nadpisań (`SALE_INVOICE_OVERRIDES`): numer
 * systemowy (mono), numer obcy (pogrubiony, nieukrywalny), nabywca (nazwa+NIP, filtr similarity
 * `vendor_name` — pole `vendor_id` niesie nabywcę po stronie sprzedaży), typ dokumentu (etykieta
 * i18n), kwota brutto (+waluta), źródło (SaleSourceBadge). Firma/opiekun idą dedykowanymi
 * budowniczymi (filtry record-select), więc są WYKLUCZONE z `buildCoreColumns` (`excludeSlugs`).
 * Kolumny procesu (aktywny proces / bieżący etap) to pola WIRTUALNE silnika (poza katalogiem) —
 * domyślnie widoczne gdy moduł procesu aktywny. Pola dodatkowe (ext) przez `buildExtColumns`.
 *
 * Brak pola `received_date` (zakupowe — data otrzymania faktury od dostawcy) — katalog sprzedaży
 * go po prostu nie definiuje, więc kolumna nigdy się nie wygeneruje.
 *
 * Widoczność: brak układu → wszystkie dostępne widoczne; układ jest DOSŁOWNY (kolumna spoza układu
 * ukryta, nie wpychamy nowych „na chama"). `?columns=` (projekcja odpowiedzi) budowany z aktualnie
 * widocznych kolumn (`computeVisibleColumnParams`); SQL SELECT rdzenia po stronie backendu zostaje pełny.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type ListActionId } from '@saas/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Receipt, RotateCcw, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { listLoadRetry } from '@/components/configurable-table/load-error';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ActionGuidanceModal } from '@/components/documents/ActionGuidanceModal';
import { BulkDecreeModal } from '@/components/documents/BulkDecreeModal';
import { BulkSendToSymfoniaModal } from '@/components/documents/BulkSendToSymfoniaModal';
import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { IconButton } from '@/components/IconButton';
import { ModuleGuard } from '@/components/ModuleGuard';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { buildDocumentCoreRefColumns } from '@/lib/documents/document-core-ref-columns';
import { buildProcessColumns } from '@/lib/documents/process-columns';
import {
  SALE_INVOICE_OVERRIDES,
  saleInvoiceExportTextOverrides,
  type SaleInvoiceRow,
  SI_ALWAYS_SLUGS,
  SI_COLUMN_COMPANIONS,
  SI_COLUMN_ID_TO_SLUG,
  SI_EXCLUDE_SLUGS,
  SI_PROCESS_LABEL_PREFIX,
  SI_SORT_SLUG_OVERRIDES,
} from '@/lib/documents/sale-invoice-columns';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useActionReadiness } from '@/lib/list-actions/use-action-readiness';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import { listFilterSkipSlugs, useListFilterOptions } from '@/lib/list-filter-options';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const SALE_INVOICES_KEY = 'sale-invoices';

/** Pola towarzyszące kolumn (nazwa/NIP nabywcy, waluta kwot) — zawsze w eksporcie CSV. */
const SI_EXPORT_COMPANION_SLUGS = SI_COLUMN_COMPANIONS.map((rule) => rule.addSlug);

// Pola rysowane nadpisaniem kolumny albo osobnym builderem nie pobierają słownika opcji filtra.
const FILTER_OPTIONS_SKIP_SLUGS = listFilterSkipSlugs(SALE_INVOICE_OVERRIDES, SI_EXCLUDE_SLUGS);

interface ListResponse {
  invoices: SaleInvoiceRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function SaleInvoices() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const companyScope = useActiveCompanyScope();
  const scopedCompanyId = companyIdParam(companyScope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  // Moduł procesu aktywny → kolumny przekrojowe (aktywny proces / bieżący etap) domyślnie widoczne
  // (gdy brak układu). Nieaktywny → domyślnie ukryte (backend i tak nie dokłada dla nich danych).
  const isProcessModuleActive = useMemo(
    () => me?.modules?.some((m) => m.code === 'processes') ?? false,
    [me?.modules],
  );

  const [showTypePicker, setShowTypePicker] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showArchived, setShowArchived] = useState(false);

  // Sort serwerowy + paginacja (silnik list). `columnIdToSortSlug`: id kolumny = slug (fallback),
  // poza remapem „Nabywca" (patrz `SI_SORT_SLUG_OVERRIDES`). `sortableSlugs` = `meta.sortable` z
  // ostatniej odpowiedzi (REALNY stan — kolumny muszą się przeliczyć, gdy metadane dotrą).
  const controls = useServerListControls({
    columnIdToSortSlug: SI_SORT_SLUG_OVERRIDES,
  });
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Filtry per kolumna z popupu w nagłówku + wiersz „Szukaj…" pod nagłówkami.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Zmiana filtra/sortu wraca na pierwszą stronę. Zawinięte tutaj, bo `headerFilters`/`columnFilters`
  // mają DWA callsite'y (`ListFilterBar` + `ConfigurableTable`).
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );
  const handleColumnFiltersChange = useCallback(
    (next: ColumnFilters) => {
      setColumnFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  // Zmiana zakresu firmy (selektor globalny) też zawęża wynik — reset strony analogicznie.
  // Zaznaczenie czyścimy, bo zawężony wynik może już nie zawierać zaznaczonych wierszy.
  useEffect(() => {
    controls.resetPage();
    setSelectedIds([]);
  }, [scopedCompanyId, controls.resetPage]);

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListTopWidgets, ListFilterBar, ConfigurableTable), inaczej
  // widżety i tabela renderują dwa różne layouty.
  const withMenuItem = useWithMenuItem();
  const forcedListLayoutId = useForcedListLayoutId('sale_invoices');
  const tableId = forcedListLayoutId
    ? `sale_invoices::layout-${forcedListLayoutId}`
    : 'sale_invoices';

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  const [confirmDelete, setConfirmDelete] = useState<SaleInvoiceRow | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<SaleInvoiceRow | null>(null);

  // Zaznaczenie wierszy (checkbox po lewej) → akcje na zaznaczonych z menu „Akcje" (PHASE 25).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Dekretacja wsadowa (akcja „Dekretuj") — `null` = zamknięty; tablica ID = otwarty na tych fakturach.
  const [decreeIds, setDecreeIds] = useState<string[] | null>(null);
  // Wysyłka do FK (akcja „Wyślij do FK”) — dwukrokowe okno: sprawdzenie na próbę → wysyłka gotowych.
  const [fkSendIds, setFkSendIds] = useState<string[] | null>(null);
  // Akcja zbramkowana brakiem konfiguracji (KSeF/Dekretuj) — otwiera modal naprowadzający.
  const [gateFor, setGateFor] = useState<ListActionId | null>(null);

  // Zbiór kolumn FAKTYCZNIE widocznych teraz — zasilany przez `onVisibleColumnsChange`. `null` przed
  // pierwszym wywołaniem → `visibleColumnParams` spada na `listLayout`/`defaultVisible`.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);

  // Katalog pól modułu `sale_invoices` (core + ext) — źródło WSZYSTKICH kolumn (rdzennych i
  // dodatkowych) generowanych dynamicznie poniżej.
  const fieldsQ = useSystemModuleFields('sale_invoices');
  // Listy wyboru w wierszu filtrów, które trzeba wczytać: pozycje słowników pól `select`
  // (status/pilność/sposób płatności) i użytkownicy dla „Opiekuna" (`useListFilterOptions`).
  const filterOptions = useListFilterOptions(fieldsQ.data, FILTER_OPTIONS_SKIP_SLUGS);
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany niżej PO `listQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const extColumns = useMemo<ColumnDefinition<SaleInvoiceRow>[]>(
    () => buildExtColumns<SaleInvoiceRow>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Firma/opiekun/kategoria wykluczone (dedykowane buildery niżej).
  const coreColumns = useMemo<ColumnDefinition<SaleInvoiceRow>[]>(
    () =>
      buildCoreColumns<SaleInvoiceRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels,
        overrides: SALE_INVOICE_OVERRIDES,
        excludeSlugs: SI_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
        selectOptions: filterOptions.selectOptions,
      }),
    [
      fieldsQ.data,
      t,
      getRefLabels,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.selectOptions,
    ],
  );

  // Kolumny przekrojowe silnika (poza katalogiem) — aktywny proces / bieżący etap (wspólna budowa
  // `buildProcessColumns`). Domyślnie widoczne gdy moduł procesu aktywny (inaczej byłyby
  // kolumnami samych myślników).
  const processColumns = useMemo<ColumnDefinition<SaleInvoiceRow>[]>(
    () =>
      buildProcessColumns<SaleInvoiceRow>({
        t,
        labelKeyPrefix: SI_PROCESS_LABEL_PREFIX,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isProcessModuleActive,
      }),
    [t, controls.isCoreColumnSortable, sortableSlugs, isProcessModuleActive],
  );

  const columns = useMemo<ColumnDefinition<SaleInvoiceRow>[]>(
    () => [
      ...coreColumns,
      buildCompanyColumn<SaleInvoiceRow>({ companies, getRefLabels, t }),
      // Kategoria — wspólny builder (`@/lib/ext-list-columns`), JEDNO źródło prawdy dla
      // Kontrahentów (pilot) + 7 klas dokumentów.
      buildCategoryColumn<SaleInvoiceRow>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      // Opiekun — core-referencja ze wspólnego buildera (6 list dokumentów dzieliło ten
      // sam blok). Faktury renderują `source` OSOBNO (rejestr nadpisań) — `withSource` domyślnie false.
      // Filtr opiekuna: lista użytkowników (`record-select`), gdy serwer go serwuje i lista się wczytała.
      ...buildDocumentCoreRefColumns<SaleInvoiceRow>({
        t,
        getRefLabels,
        resolveOwnerFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        ownerFilterOptions: filterOptions.ownerOptions,
      }),
      ...processColumns,
      ...extColumns,
    ],
    [
      coreColumns,
      companies,
      getRefLabels,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.ownerOptions,
      processColumns,
      extColumns,
    ],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug wszędzie poza „Firmą" (id `company`).
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...SI_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Kolumny formatowania warunkowego mogą celować w kolumnę NIEwidoczną — `extraColumnIds` dopisuje
  // jej slug do `?columns=`, żeby reguła miała dane do ewaluacji.
  const formattingRuleColumnIds = useMemo(
    () => listLayout?.layoutConfig?.formattingRules?.map((r) => r.columnId) ?? [],
    [listLayout?.layoutConfig?.formattingRules],
  );

  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns:
          visibleColumnIds !== null
            ? columns.map((c) => ({ id: c.id, visible: visibleColumnIds.includes(c.id) }))
            : listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        // `gcrecord`/`company_id` zawsze + reguły towarzysza (nazwa/NIP nabywcy, waluta kwot) —
        // patrz `SI_ALWAYS_SLUGS` / `SI_COLUMN_COMPANIONS`.
        alwaysSlugs: SI_ALWAYS_SLUGS,
        companions: SI_COLUMN_COMPANIONS,
        extraColumnIds: formattingRuleColumnIds,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      visibleColumnIds,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
      formattingRuleColumnIds,
    ],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku + (2) input „Szukaj…" pod headerem (→ condition contains) + (3) szybki filtr.
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }

    return merged;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy. Eksport CSV (E16) bierze same filtry widoku: miesiąc to nawigacja.
  const exportHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const serializedHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : exportHeaderFilters;

  // Parametry filtrów listy BEZ stronicowania i projekcji kolumn — wspólne dla zapytania listy
  // i eksportu CSV (E16: plik zawiera dokładnie to, co lista, łącznie z przełącznikiem
  // „Pokaż zarchiwizowane"). Zwykła funkcja, nie hook: czyta stan strony w chwili wywołania.
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const listHeaderFilters = includeCalendar ? serializedHeaderFilters : exportHeaderFilters;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (showArchived) params.set('includeArchived', 'true');
    if (listHeaderFilters) params.set('headerFilters', listHeaderFilters);
    if (scopedCompanyId) params.set('companyId', scopedCompanyId);
    if (controls.queryParams.sort) params.set('sort', controls.queryParams.sort);
    params.set('order', controls.queryParams.order);
    return params;
  }

  const queryKey = [
    SALE_INVOICES_KEY,
    {
      q: debouncedSearch,
      includeArchived: showArchived,
      headerFilters: serializedHeaderFilters,
      companyScope: scopedCompanyId ?? 'all',
      columns: visibleColumnParams,
      ...controls.queryParams,
    },
  ];

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane podczas refetchu wywołanego zmianą klucza (widoczne kolumny się
    // „ustalają" asynchronicznie) — bez tego lista miga loaderem przy każdym wejściu.
    placeholderData: keepPreviousData,
    // Awaria → jedno automatyczne ponowienie; zły adres / brak uprawnień / nieaktywny moduł →
    // bez ponawiania (komunikat od razu).
    retry: listLoadRetry,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));
      const res = await api.get<ListResponse>(`/sale-invoices?${params.toString()}`);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable` w stan przez EFEKT, nie w renderze (to useState → wywołanie w renderze pętli).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Usuwanie i przywracanie z listy to zwykły zapis dokumentu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje dokument.
  const reportRecordLock = useReportRecordLock();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sale-invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SALE_INVOICES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'sale_invoices'] });
      toast.show({ variant: 'success', title: t('tenant.saleInvoices.toast.deleted') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.saleInvoices.toast.deleteError') });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/sale-invoices/${id}/restore`, {});
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: [SALE_INVOICES_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['sale-invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['distinct-values', 'sale_invoices'] });
      toast.show({ variant: 'success', title: t('tenant.saleInvoices.toast.restored') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.saleInvoices.toast.restoreError') });
    },
  });

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności (katalog ∩ kartoteka ∩
  // moduły ∩ uprawnienia) + wykonawca (rozgłoszenie po ID na istniejące endpointy) + menu
  // kontekstowe (prawy klik) + potwierdzenia. Kartoteka `documents`, klasa `sale_invoice` →
  // „Wyślij do KSeF" (moduł ksef_integration) i „Wyślij do FK" (symfonia_integration) widoczne
  // przy aktywnych modułach i uprawnieniach. Eksport/import bez handlera → „wkrótce". BEZ bramki
  // „Wszystkie firmy": archiwizacja/przywróć/KSeF/FK biorą firmę z samego dokumentu (nie z
  // nagłówka X-Company-Id), więc działają też w trybie unii firm.
  // Gotowość konfiguracji dla aktywnej firmy — steruje wyszarzeniem KSeF/Dekretuj + modalem.
  const readiness = useActionReadiness(scopedCompanyId ?? null, 'sale_invoice');
  const gatedActions = useMemo<ListActionId[]>(() => {
    if (readiness.isLoading) return [];
    const g: ListActionId[] = [];
    if (!readiness.ksefConfigured) g.push('ksef_send');
    if (!readiness.fkConfigured) g.push('fk_send');
    if (!readiness.hasChart || !readiness.hasDecreeRule) g.push('decree');
    return g;
  }, [readiness]);

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie
  // listy, wszystkie strony. „Eksport zaznaczonych": tylko wskazane ID (+ firma, sort i przełącznik
  // archiwum — zaznaczone faktury zarchiwizowane nie mogą wypaść z pliku). Plik zawiera WYŁĄCZNIE
  // kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami; sumy pod listą do pliku nie idą.
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);
  const listExport = useEngineListExport<SaleInvoiceRow>({
    target: 'sale-invoices',
    filename: 'faktury-sprzedazy',
    // Bez miesiąca kalendarza — to nawigacja widoku, nie filtr (plik = cały widok).
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    selectedKeepParams: ['companyId', 'sort', 'order', 'includeArchived'],
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    // Pola towarzyszące (jak `?columns=` listy): „Nabywca" czyta nazwę i NIP, kwoty — walutę.
    // Dokładane zawsze — plik i tak zawiera tylko widoczne kolumny.
    extraSlugs: SI_EXPORT_COMPANION_SLUGS,
    // Etykiety odwołań z odpowiedzi eksportu (wszystkie wiersze, nie bieżąca strona) — te same
    // buildery co tabela.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return [
        ...buildCoreColumns<SaleInvoiceRow>({
          fields: fieldsQ.data ?? [],
          t,
          getRefLabels: getLabels,
          overrides: SALE_INVOICE_OVERRIDES,
          excludeSlugs: SI_EXCLUDE_SLUGS,
          // Ten sam słownik etykiet co tabela — pola wyboru ze słownika (np. „Pilność") do pliku
          // jako nazwa pozycji, nie kod.
          selectOptions: filterOptions.selectOptions,
        }),
        buildCompanyColumn<SaleInvoiceRow>({ companies, getRefLabels: getLabels, t }),
        buildCategoryColumn<SaleInvoiceRow>({ t }),
        ...buildDocumentCoreRefColumns<SaleInvoiceRow>({ t, getRefLabels: getLabels }),
        ...processColumns,
        ...buildExtColumns<SaleInvoiceRow>(fieldsQ.data ?? [], t, getLabels),
      ];
    },
    textOverrides: saleInvoiceExportTextOverrides(t),
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.saleInvoices.title'),
  });

  const listActions = useListActions({
    kartotekaKey: 'documents',
    documentClass: 'sale_invoice',
    entity: 'sale-invoices',
    // `tableId` listy = `sale_invoices` (podkreślenie), a route/`entity` = `sale-invoices`
    // (myślnik). Distinct-values rejestruje się pod `tableId`, więc podajemy go jawnie, żeby
    // dropdowny wartości filtra odświeżyły się po archiwizacji/przywróceniu (parytet z dawną
    // per-stronową inwalidacją `['distinct-values','sale_invoices']`).
    distinctValuesKey: ['distinct-values', 'sale_invoices'],
    queryKey: [SALE_INVOICES_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    // Akcja „Dekretuj" jest modalowa (jak import) — otwiera okno wsadowej dekretacji.
    // „Wyślij do FK” też modalowa — generyczne rozgłoszenie bez body robiło tylko sprawdzenie
    // na próbę (nic nie trafiało do Symfonii), a lista meldowała „Gotowe”.
    handlers: {
      ...listExport.handlers,
      decree: (ids) => setDecreeIds(ids),
      fk_send: (ids) => setFkSendIds(ids),
    },
    gatedActions,
    onGatedClick: (actionId) => setGateFor(actionId),
    archivedView: showArchived,
    testIdPrefix: 'sale-invoices',
  });

  const invoices = listQ.data?.invoices ?? [];

  return (
    <ModuleGuard moduleCode="crm_core">
      <main
        className="flex h-full min-h-0 flex-col bg-bg-secondary p-6"
        data-testid="sale-invoices-page"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div>
              <h1
                className="text-2xl font-bold text-text-primary"
                data-testid="sale-invoices-title"
              >
                {t('tenant.saleInvoices.title')}
              </h1>
              <p className="mt-1 text-sm text-text-tertiary">{t('tenant.saleInvoices.subtitle')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="sale-invoices" />
              <ListActionsMenu {...listActions.menuProps} />
              <ListBuilderMenu slug="sale_invoices" testIdPrefix="sale-invoices" />
              <Button
                onClick={() => setShowTypePicker(true)}
                leftIcon={<Plus size={16} aria-hidden="true" />}
                data-testid="sale-invoices-add-btn"
              >
                {t('tenant.saleInvoices.addButton')}
              </Button>
            </div>
          </header>

          <div className="shrink-0">
            <ListTopWidgets tableId={tableId} />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {fieldsQ.isError ? (
              <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
            ) : (
              <ConfigurableTable<SaleInvoiceRow>
                tableId={tableId}
                testIdPrefix="sale-invoices"
                showOrdinal
                fillHeight
                {...viewModes.tableProps}
                /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): segmented po lewej,
                   baton odśwież/filtry po prawej. */
                listToolbar={
                  <div className="flex flex-wrap items-center gap-2">
                    <QuickFilterBar
                      quickFilters={quickFilters}
                      activeId={activeQuickFilterId}
                      onChange={(id) => {
                        setActiveQuickFilterId(id);
                        controls.resetPage();
                      }}
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.saleInvoices.searchPlaceholder')}
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: [SALE_INVOICES_KEY] })
                        }
                        showArchived={showArchived}
                        onToggleArchived={(next) => {
                          setShowArchived(next);
                          controls.resetPage();
                          setSelectedIds([]);
                        }}
                        testIdPrefix="sale-invoices"
                      />
                    </div>
                  </div>
                }
                columns={columns}
                data={invoices}
                isLoading={listQ.isPending}
                getRowId={(row) => row.id}
                headerFilters={headerFilters}
                onHeaderFiltersChange={handleHeaderFiltersChange}
                filterable
                columnFilters={columnFilters}
                onColumnFiltersChange={handleColumnFiltersChange}
                onVisibleColumnsChange={setVisibleColumnIds}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onRowContextMenu={listActions.onRowContextMenu}
                {...controls.tableProps}
                totalItems={listQ.data?.total ?? 0}
                isFetching={listQ.isFetching}
                loadError={listQ.error}
                onRetryLoad={() => void listQ.refetch()}
                onCalendarRangeChange={controls.setCalendarRange}
                onRowClick={(row) => navigate(withMenuItem(`/sale-invoices/${row.id}`))}
                renderActions={(row) => (
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {row.gcrecord !== null ? (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmRestore(row)}
                        disabled={restoreMutation.isPending}
                        aria-label={t('tenant.saleInvoices.actions.restore')}
                        data-testid={`sale-invoices-row-${row.id}-restore`}
                      >
                        <RotateCcw aria-hidden="true" />
                      </IconButton>
                    ) : (
                      <IconButton
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmDelete(row)}
                        disabled={deleteMutation.isPending}
                        aria-label={t('common.delete')}
                        data-testid={`sale-invoices-row-${row.id}-delete`}
                      >
                        <Trash2 aria-hidden="true" />
                      </IconButton>
                    )}
                  </div>
                )}
                actionsColumnLabel={t('tenant.saleInvoices.columns.actions')}
                emptyIcon={Receipt}
                emptyTitle={t('tenant.saleInvoices.empty')}
              />
            )}
          </div>
        </div>

        <ConfirmDialog
          open={confirmDelete !== null}
          title={
            confirmDelete
              ? t('tenant.saleInvoices.deleteConfirm', {
                  number: confirmDelete.systemNumber ?? confirmDelete.invoiceNumber,
                })
              : ''
          }
          message={t('tenant.saleInvoices.deleteConfirmBody')}
          confirmLabel={t('common.delete')}
          variant="danger"
          onConfirm={() => {
            if (confirmDelete) {
              deleteMutation.mutate(confirmDelete.id);
              setConfirmDelete(null);
            }
          }}
          onClose={() => setConfirmDelete(null)}
        />

        <ConfirmDialog
          open={confirmRestore !== null}
          title={
            confirmRestore
              ? t('tenant.saleInvoices.restoreConfirm', {
                  number: confirmRestore.systemNumber ?? confirmRestore.invoiceNumber,
                })
              : ''
          }
          message={t('tenant.saleInvoices.restoreConfirmBody')}
          confirmLabel={t('tenant.saleInvoices.actions.restore')}
          variant="primary"
          onConfirm={() => {
            if (confirmRestore) {
              restoreMutation.mutate(confirmRestore.id);
              setConfirmRestore(null);
            }
          }}
          onClose={() => setConfirmRestore(null)}
        />
      </main>
      <DocumentTypePickerModal
        open={showTypePicker}
        docClass="sale_invoice"
        onSelect={(typeId, companyId) => {
          setShowTypePicker(false);
          navigate(
            withMenuItem(`/sale-invoices/new?documentTypeId=${typeId}&companyId=${companyId}`),
          );
        }}
        onClose={() => setShowTypePicker(false)}
      />
      {listActions.overlays}
      <BulkDecreeModal
        open={decreeIds !== null}
        onClose={() => setDecreeIds(null)}
        documentIds={decreeIds ?? []}
        documentClass="sale_invoice"
        companyIds={Array.from(
          new Set(
            invoices
              .filter((inv) => (decreeIds ?? []).includes(inv.id))
              .map((inv) => inv.companyId)
              .filter((c): c is string => !!c),
          ),
        )}
        onDone={() => {
          setSelectedIds([]);
          void listQ.refetch();
        }}
      />
      <BulkSendToSymfoniaModal
        open={fkSendIds !== null}
        onClose={() => setFkSendIds(null)}
        documents={(fkSendIds ?? []).map((id) => {
          const inv = invoices.find((i) => i.id === id);
          return { id, label: inv?.invoiceNumber || inv?.systemNumber || id.slice(0, 8) };
        })}
        detailPath={(id) => withMenuItem(`/sale-invoices/${id}`)}
        onDone={() => {
          setSelectedIds([]);
          void listQ.refetch();
        }}
      />
      <ActionGuidanceModal
        open={gateFor !== null}
        actionId={gateFor}
        readiness={readiness}
        onClose={() => setGateFor(null)}
      />
    </ModuleGuard>
  );
}
